import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent } from './primitives.ts'

/** ══ HỢP ĐỒNG API — NGƯỜI DÙNG · CẤU HÌNH CÔNG TY · CẤP DUYỆT · CỔNG THẦU PHỤ ═══════════════ */

const appUserFields = {
  shortName: z.string().optional(),
  code: z.string().optional(),
  phone: z.string().optional(),
  bankAccount: z.string().optional(),
  bankName: z.string().optional(),
  apScope: z.enum(['own', 'all']).nullable().optional(),
  azSalesScope: z.enum(['own', 'all']).optional(),
  azSeeVendorCost: z.boolean().optional(),
  azQuoteFromCost: z.boolean().optional(),
  status: z.string().optional(),
  active: z.boolean().optional(),
  /** Ngày vào làm — `''` xoá trắng. */
  hireDate: z.union([ISODate, z.literal('')]).nullable().optional(),
  /** Target doanh số tháng (đồng) — `''` xoá trắng. */
  monthlyTarget: z.union([z.string().regex(/^\d+(\.\d{1,2})?$/, 'Target tháng phải là số tiền.'), z.literal('')]).nullable().optional(),
  /** Đổi loại hợp đồng ⇒ máy chủ thêm kỳ mới từ hôm nay (giữ lịch sử). */
  empType: z.enum(['staff', 'ctv']).optional(),
}

/**
 * Bảy vai NỘI BỘ của RT — đúng `STAFF_ROLES` (`@rs/policy/routes`; gói này không import được
 * `@rs/policy`, nên ca kiểm `identity/domain/user-access.test.ts` ghim hai danh sách bằng nhau).
 * ⚠ C4 (soi 16/10): trước đây `z.string()` ⇒ vai lạ lọt tới khoá ngoại `app_role` và nổ 500. Vai
 *   `ncc` KHÔNG có ở đây: thầu phụ là `vendor_user`, không phải nhân sự.
 */
export const StaffRole = z.enum([
  'admin', 'giam_doc', 'ke_toan_truong', 'ke_toan', 'truong_phong_kd', 'dieu_hanh', 'sale',
], { errorMap: () => ({ message: 'Vai không hợp lệ.' }) })

export const CreateAppUserBody = z.object({
  email: z.string(),
  name: z.string(),
  role: StaffRole,
  ...appUserFields,
})

/** ⚠ KHÔNG có `email`: đổi email là đổi danh tính, phải đi đường khác (`UserPatch` bỏ `email`). */
export const UpdateAppUserBody = z.object({
  version: Version.optional(),
  name: z.string().optional(),
  role: StaffRole.optional(),
  ...appUserFields,
})

export const CompanySettingsBody = z.object({
  divisionName: z.string().optional(),
  displayName: z.string().optional(),
  bctcTemplateUrl: z.string().optional(),
  // ── ĐẦU THƯ IN TRÊN HỢP ĐỒNG / BÁO GIÁ / VẬN ĐƠN (F11) ──
  // Chép `PRINT_CO_DEFAULT` (`proship-trans/src/lib/azPrintDocs.ts:11`). Ô VẮNG MẶT = không đụng
  // tới; ô gửi lên CHUỖI RỖNG = xoá trắng. Ba ô cũ ở trên giữ nguyên ngữ nghĩa "gửi gì ghi nấy"
  // của D06 — đổi chúng là đổi hành vi một API đang chạy.
  printCompanyName: z.string().optional(),
  printCompanyAddr: z.string().optional(),
  printCompanyTax: z.string().optional(),
  printCompanyPhone: z.string().optional(),
  printCompanyEmail: z.string().optional(),
  printCompanyWeb: z.string().optional(),
  // ── TÀI KHOẢN NHẬN TIỀN CỦA MÌNH + GIÁM ĐỐC KÝ, in trên ĐNTT và bảng kê bán (`0069`) ──
  // Cùng ngữ nghĩa "ô vắng mặt = không đụng tới" với sáu ô đầu thư ngay trên.
  payAcctName: z.string().optional(),
  payAcctNo: z.string().optional(),
  payBankName: z.string().optional(),
  payBankAddr: z.string().optional(),
  directorName: z.string().optional(),
  // ── ĐỊA CHỈ NHẬN BÁO CÁO DÒNG TIỀN CHO CEO (`0071`) ──
  // Bản cũ giữ MỘT bản ghi khoá cố định `id = 'ceo'` với `email` + `cc` trong kho trình duyệt.
  ceoReportEmail: z.string().optional(),
  ceoReportCc: z.string().optional(),
  // ── TỜ IN BẢNG GIÁ CÔNG BỐ ĐƯỜNG SẮT (`0072`) ──
  railDocTitle: z.string().optional(),
  railDocSourceNote: z.string().optional(),
  railDocEffectiveFrom: z.string().optional(),
})

export type CompanySettingsBodyT = z.infer<typeof CompanySettingsBody>

/**
 * ✉ T24 (rà 16/10, chốt 17/10) — HAI CỬA HẸP của cấu hình công ty, mỗi cửa chỉ ghi ĐÚNG nhóm ô của nó (cửa toàn phần
 * `PUT /settings/company` vẫn chỉ admin/GĐ). Mọi ô BẮT BUỘC: hộp luôn gửi đủ nhóm của mình, chuỗi rỗng = xoá trắng.
 */
export const CeoMailBody = z.object({
  ceoReportEmail: z.string().max(500),
  ceoReportCc: z.string().max(500),
})
export type CeoMailBodyT = z.infer<typeof CeoMailBody>
export const PayAccountBody = z.object({
  payAcctName: z.string().max(200),
  payAcctNo: z.string().max(60),
  payBankName: z.string().max(200),
  payBankAddr: z.string().max(300),
  directorName: z.string().max(200),
})
export type PayAccountBodyT = z.infer<typeof PayAccountBody>

/**
 * NỐI TÀI KHOẢN THẦU PHỤ VÀO DANH MỤC NCC (F13).
 *
 * ⚠ `vendorId` NHẬN `null` TƯỜNG MINH để GỠ nối — khác hẳn "không gửi ô này". Gỡ nối là một thao
 *   tác thật (thầu phụ nghỉ hợp tác), và nó phải khác với việc quên điền.
 */
export const LinkVendorUserBody = z.object({
  email: z.string().min(1, 'Thiếu email tài khoản thầu phụ.'),
  vendorId: Uuid.nullable(),
})
export type LinkVendorUserBodyT = z.infer<typeof LinkVendorUserBody>

/**
 * CẤP TÀI KHOẢN CỔNG THẦU PHỤ (E17). Không có ô mật khẩu: máy chủ tự sinh và trả về ĐÚNG MỘT LẦN.
 * `name` bỏ trống ⇒ lấy tên NCC.
 */
export const CreateVendorUserBody = z.object({
  vendorId: Uuid,
  email: z.string().min(1, 'Thiếu email đăng nhập của thầu phụ.'),
  name: blankAsAbsent(z.string()),
  phone: blankAsAbsent(z.string()),
})
export type CreateVendorUserBodyT = z.infer<typeof CreateVendorUserBody>

export const VendorUserIdParams = z.object({ id: Uuid })

/** Cấp lại mật khẩu không nhận gì — mật khẩu do máy chủ sinh. */
export const ResetVendorPasswordBody = z.object({}).strict()

export const SetVendorUserActiveBody = z.object({
  version: Version,
  active: z.boolean(),
})

export const UpdateApprovalRuleBody = z.object({
  version: Version.optional(),
  roles: z.array(z.string()).optional(),
  defaultAssigneeId: blankAsAbsent(Uuid.nullable()),
  extraUserIds: z.array(Uuid).optional(),
})

export const VendorLoginBody = z.object({
  email: z.string(),
  password: z.string(),
})

export const VendorChangePasswordBody = z.object({
  oldPassword: z.string(),
  newPassword: z.string(),
})

/**
 * ĐĂNG NHẬP BẰNG TÀI KHOẢN cho nhân sự nội bộ (E13 sửa 19/10/2026) — lựa chọn thứ hai bên cạnh FADO SSO.
 * Không có ô vai: vai lấy từ `app_user.role`, không bao giờ nhận từ trình duyệt.
 */
export const PasswordLoginBody = z.object({
  email: z.string().min(1, 'Nhập email.'),
  password: z.string().min(1, 'Nhập mật khẩu.'),
})

/**
 * Tự đặt / đổi mật khẩu của chính mình. `oldPassword` vắng mặt CHỈ hợp lệ khi tài khoản chưa có mật khẩu — máy chủ
 * tự kiểm (đã có mật khẩu mà không gửi ⇒ "Mật khẩu hiện tại không đúng").
 */
export const ChangeOwnPasswordBody = z.object({
  oldPassword: blankAsAbsent(z.string()),
  newPassword: z.string(),
})

/** Cấp lại mật khẩu tạm cho người khác — không nhận gì, mật khẩu do máy chủ sinh (CSPRNG — E25). */
export const ResetUserPasswordBody = z.object({}).strict()

/** Tuỳ chọn giao diện của chính người đang đăng nhập. */
export const PrefsLangBody = z.object({ lang: z.string().optional() })

/**
 * XOÁ hồ sơ nhân sự — `POST …/:id/delete` kèm `version`, đúng lối đã dùng ở mẫu chứng từ
 * (`DocTemplateStepBody`): cửa `DELETE` không mang thân request được, mà không có `version` thì
 * không phát hiện được hai người cùng sửa một hồ sơ.
 */
export const DeleteAppUserBody = z.object({ version: Version })

export const UserIdParams = z.object({ id: Uuid })
export const ListUserQuery = PageQuery
export type ListUserQueryT = z.infer<typeof PageQuery>

/* ══ CHUÔNG THÔNG BÁO (`0070`) ═══════════════════════════════════════════════════════════════ */

/**
 * ⚠ KHÔNG có tham số NGƯỜI NHẬN ở bất kỳ cửa nào — cả ba cửa đều làm việc với thông báo CỦA CHÍNH
 *   người đang đăng nhập. Thêm một ô `userId` vào đây là mở đường đọc chuông của người khác.
 */
export const NotifQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
})

export const NotifIdParams = z.object({ id: Uuid })

/**
 * ⚠ THÂN RỖNG NHƯNG PHẢI KHAI. Cổng `http/validate.ts` đòi MỌI route ghi dưới `/api/v1` có lược
 *   đồ thân — route nào thật sự không nhận gì thì khai một đối tượng rỗng, chứ không xin miễn:
 *   miễn trừ là một lỗ hổng, khai rỗng thì vẫn chặn được thân lạ gửi kèm.
 */
export const EmptyBody = z.object({}).strict()

export type NotifQueryT = z.infer<typeof NotifQuery>
export type NotifIdParamsT = z.infer<typeof NotifIdParams>
